<template>
  <div class="base-layout">
    <action-bar
      app-title="Easy Design-In Tool"
      :show-controls="showControls"
      :user-data="userData"
      :show-dropdown-on-anonymous="true"
      :search-placeholder="'You can search for: module, driver, node, bridge, 12NC, etc'"
      @userIconClicked="onUserIconClicked"
      @globalSearch="globalSearch"
      @logoAction="logoClicked"
      ref="actionBar"
    >
      <template #actions>
        <button
          type="button"
          class="action-item guide-me btn btn-sm btn-outline-primary"
          v-modal-link="{ name: 'guide', props: { show: true } }"
        >
          Guide me
        </button>
        <div class="restart-container" v-if="$route.name === 'advanced'">
          <span class="restart" @click="restart">Restart</span>
        </div>
      </template>
      <template #dropdown-user>
        <div v-if="!isLoggedIn">
          <a class="login-link" href="#" @click="oidcSigninRedirect">Log In</a>
          <ul>
            <li>
              <logout-svg class="dropdown-item-icon"></logout-svg>
              <a href="javascript:void(0)" @click="showAvailableRegions">{{
                activeRegion
              }}</a>
            </li>
            <li>
              <contact-svg class="dropdown-item-icon"></contact-svg>
              <a class="contact-us" @click="onContactUs" href="#">Contact</a>
            </li>
          </ul>
        </div>
        <ul v-else>
          <li>
            <solution-svg class="dropdown-item-icon"></solution-svg>
            <a href="#" @click="savedSolutionsClicked">My solutions</a>
          </li>
          <li>
            <filter-svg class="dropdown-item-icon"></filter-svg>
            <a href="#" @click="loadFiltersClicked">My filters</a>
          </li>
          <li>
            <reset-svg class="dropdown-item-icon"></reset-svg>
            <a href="#" @click="showViewOptions">My view options</a>
          </li>
          <li v-if="canI('MAINTENANCE_MODE_FRONTEND')">
            <maintenance-svg class="dropdown-item-icon"></maintenance-svg>
            <a href="#" @click="toggleMaintenanceMode">
              <span v-if="maintenanceMode">Disable review mode</span>
              <span v-if="!maintenanceMode">Enable review mode</span>
            </a>
          </li>
          <li v-if="canI(ADMIN_PANEL)">
            <maintenance-svg class="dropdown-item-icon"></maintenance-svg>
            <a :href="adminPanelURL">Admin panel</a>
          </li>
          <li>
            <globe-svg class="dropdown-item-icon"></globe-svg>
            <a href="javascript:void(0)" @click="showAvailableRegions">{{
              activeRegion
            }}</a>
          </li>
          <li>
            <contact-svg class="dropdown-item-icon"></contact-svg>
            <a class="contact-us" @click="onContactUs" href="#">Contact</a>
          </li>
          <li v-if="isLoggedIn">
            <logout-svg class="dropdown-item-icon"></logout-svg>
            <a href="#" @click="oidcSignoutRedirect">Log out</a>
          </li>
        </ul>
      </template>
      <template #dropdown-apps>
        <ul>
          <li v-for="app in apps" :key="app.name">
            <a
              v-if="app.visible"
              :href="app.url()"
              target="_blank"
              rel="noopener noreferrer"
              >{{ app.name }}</a
            >
          </li>
        </ul>
      </template>
    </action-bar>
    <div>
      <notification-bar v-if="maintenanceMode" :mode="'maintenance'">
        review mode is enabled
      </notification-bar>
      <notification-bar v-if="impersonateData.auth.token" :mode="'default'">
        <span v-if="impersonateData.firstName"
          >You are impersonating {{ impersonateData.firstName }}
          {{ impersonateData.lastName }}.</span
        >
        <span v-else>You are impersonating a foreign portfolio.</span>
        <span class="stop-impersonation" @click="onStopImpersonating"
          >Stop Impersonating</span
        >
      </notification-bar>
      <notification-bar v-if="administerData.auth.token" :mode="'default'">
        <span v-if="administerData.companyName"
          >You are now managing the foreign portfolio of:
          {{ administerData.companyName }}</span
        >
        <span v-else>You are now managing a foreign portfolio.</span>
        <span class="stop-impersonation" @click="onStopAdministering"
          >Stop Administering</span
        >
      </notification-bar>
    </div>
    <router-view />
  </div>
</template>

<script>
import Guide from "@/views/guide/Guide";
import ContactUsModal from "@/components/modals/ContactUsModal";
import SaveFiltersetModal from "@/components/modals/SaveFiltersetModal";
import SaveViewModal from "@/components/modals/SaveViewModal";
import ChangeRegionModal from "@/components/modals/ChangeRegionModal";
import ViewOptionsModal from "@/components/modals/ViewOptionsModal";
import settings from "@/service/Settings";
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/eventbus.js";
import {
  ADMIN_PANEL,
  REGION_CN,
  REGION_EMEA,
  REGION_NA,
  REGION_ROA,
  REGION_SA,
} from "@/service/Constants";

import solutionSvg from "@/assets/svg/icons/signify-icons_18-doc-16px.svg?inline";
import filterSvg from "@/assets/svg/icons/signify-icons_31-filter-24px.svg?inline";
import maintenanceSvg from "@/assets/svg/icons/signify-icons_42-maintenance-16px.svg?inline";
import resetSvg from "@/assets/svg/icons/signify-icons_41-reset-16px.svg?inline";
import logoutSvg from "@/assets/svg/icons/signify-icons_13-arrow-button-16px.svg?inline";
import contactSvg from "@/assets/svg/icons/signify-icons_22-mail-16px.svg?inline";
import globeSvg from "@/assets/svg/icons/signify-icons_43-globe-16px.svg?inline";

export default {
  name: "BaseLayout",
  modals: {
    Guide,
    SaveFiltersetModal,
    SaveViewModal,
    ChangeRegionModal,
    ViewOptionsModal,
    ContactUsModal,
  },
  computed: {
    ...mapGetters("brain/ui", ["showControls", "showModules", "showDrivers"]),
    ...mapGetters("brain/auth", ["isImpersonating", "isLoggedIn", "userData"]),
    ...mapState("brain/auth", ["impersonateData", "administerData"]),
    ...mapState("brain", ["region", "allRegions", "maintenanceMode"]),
    ...mapGetters("brain/global", ["allFilters"]),
    ...mapGetters("brain/auth", ["activeToken"]),
    ...mapGetters("brain/solutions", ["solution", "solutions", "active"]),
    activeRegion() {
      return this.allRegions[this.region].label;
    },
    apps() {
      return [
        {
          name: "Sample web shop",
          url: () =>
            this.isAcc
              ? "http://philips-oemshop-acc.jelastic.trimm.net"
              : "https://www.emea.oemwebshop.lighting.philips.com",
          visible: this.region !== REGION_NA,
        },
        {
          name: "MyLighting",
          url: () => "https://www.mylighting.signify.com",
          visible: this.region !== REGION_SA && this.region !== REGION_ROA,
        },
        {
          name: "OEM general website",
          url: () => {
            switch (this.region) {
              case REGION_EMEA:
                return "https://www.lighting.philips.co.uk/oem-emea";
              case REGION_NA:
                return "https://www.signify.com/en-us/portfolio/oem";
              case REGION_CN:
                return "https://www.lighting.philips.com.cn/oem-cn";
              case REGION_ROA:
                return "https://www.lighting.philips.com.sg/oem-asia";
            }
          },
          visible: this.region !== REGION_SA,
        },
      ];
    },
    isAcc() {
      return settings.ENV !== "production";
    },
    adminPanelURL() {
      return settings.EDIT_ADMIN_PANEL_URL;
    },
  },
  mounted() {
    this.$store.commit("brain/ui/baseLayoutMounted", true);
  },
  data: function () {
    return {
      ADMIN_PANEL: ADMIN_PANEL,
      environment: settings.ENV,
      sso_url: settings.PORTAL_TOOLS_SSO_URL,
    };
  },
  components: {
    solutionSvg,
    filterSvg,
    maintenanceSvg,
    resetSvg,
    logoutSvg,
    contactSvg,
    globeSvg,
  },
  methods: {
    ...mapActions("brain/auth", ["oidcSigninRedirect", "oidcSignoutRedirect"]),
    onContactUs() {
      this.$modalRouter.push({
        name: "ContactUsModal",
      });
    },
    closeDropdown(e) {
      e.stopPropagation();
      this.$refs.actionBar.closeDropdown();
    },
    onUserIconClicked() {
      if (settings.ENV === "local") {
        //on acc2 and local go to dummylogin, on live go to real external login
        this.$router.push({ name: "dummyAuthLogin" });
      } else {
        console.log("real auth not implemented yet");
        this.$snotify.error("real auth not implemented yet");
      }
    },
    logoClicked() {
      if (this.$router.currentRoute.name !== "landing") {
        this.$router.push({ name: "landing" });
      }
    },
    toggleMaintenanceMode(e) {
      this.$store.commit("brain/maintenanceMode", !this.maintenanceMode);
      EventBus.$emit("recalculate", this.$router.currentRoute.name);
      this.closeDropdown(e);

      this.maintenanceMode
        ? this.$snotify.success("Review mode enabled")
        : this.$snotify.success("Review mode disabled");
    },
    onModuleShowChange() {
      this.$store.dispatch("brain/ui/showModules", {
        show: !this.showModules,
        save: true,
      });
    },
    onDriverShowChange() {
      this.$store.dispatch("brain/ui/showDrivers", {
        show: !this.showDrivers,
        save: true,
      });
    },
    loadFiltersClicked() {
      if (!this.isLoggedIn) {
        this.$snotify.error(
          "Loading filters is only available for logged in users"
        );
      } else {
        this.$router.push({ name: "savedFilters" });
      }
    },
    saveViewClicked() {
      if (!this.isLoggedIn) {
        this.$snotify.error(
          "Saving view is only available for logged in users"
        );
      } else {
        this.$modalRouter.push({
          name: "SaveViewModal",
          props: { show: true },
          model: "show",
          on: { onSaveView: this.onSaveView },
        });
      }
    },
    loadViewClicked() {
      if (!this.isLoggedIn) {
        this.$snotify.error(
          "Loading view is only available for logged in users"
        );
      } else {
        this.$router.push({ name: "savedViews" });
      }
    },
    onSaveView(description) {
      this.$store
        .dispatch("brain/global/saveAllDisplaySettings", description)
        .then((response) => {
          EventBus.$emit("viewSaved");
          this.$snotify.success("View saved successfully");
          console.log("save view response: ", response);
        });
    },
    savedSolutionsClicked(e) {
      if (!this.isLoggedIn) {
        this.$snotify.error(
          "Loading solutions is only available for logged in users"
        );
      } else {
        this.$router.push({ name: "savedSolutions" });
        this.closeDropdown(e);
      }
    },
    resetViewSettings() {
      this.$store.commit("brain/ui/setPaneSize", { size: 50 });
      this.$store.commit("brain/ui/minimizeInput", {
        type: "module",
        minimize: false,
      });
      this.$store.commit("brain/ui/minimizeInput", {
        type: "driver",
        minimize: false,
      });
      this.$store.dispatch("brain/ui/showModules", { show: true });
      this.$store.dispatch("brain/ui/showDrivers", { show: true });
      this.$store.commit("brain/ui/resetColumnVisibility", { save: true });
      EventBus.$emit("resizeColumns");
    },
    onStopImpersonating() {
      this.$store.dispatch("brain/auth/stopImpersonating").then(() => {
        this.$store.dispatch("brain/solutions/clearSolutions");
        EventBus.$emit("restart");
      });
    },
    onStopAdministering() {
      this.$store.dispatch("brain/auth/stopAdministering").then(() => {
        this.$store.dispatch("brain/solutions/clearSolutions");
        EventBus.$emit("restart");
      });
    },
    onLoadSelection() {},
    changeRegion(region) {
      this.$store
        .dispatch(
          "brain/switchRegion",
          { region: region, clearSolution: true },
          { root: true }
        )
        .then(() => {
          EventBus.$emit("recalculate", this.$router.currentRoute.name);
          EventBus.$emit("filtersSaved");
          EventBus.$emit("solutionsSaved");
          EventBus.$emit("viewSaved");
        });
    },
    globalSearch(searchTerm) {
      if (this.$router.currentRoute.name !== "search") {
        let routeName = null;
        if (this.$route.name === "landing") {
          routeName = "/";
        } else {
          routeName = this.$route.name;
        }
        this.$router.push({
          name: "search",
          params: { searchTerm: searchTerm, back: routeName },
        });
      } else {
        EventBus.$emit("globalSearch", searchTerm);
      }
    },
    showAvailableRegions() {
      this.$modalRouter.push({
        name: "ChangeRegionModal",
        props: {
          show: true,
          modalTitle: "Select your region",
          allRegions: this.allRegions,
          currentRegion: this.region,
        },
        model: "show",
        on: { changeRegion: this.changeRegion },
      });
    },
    showViewOptions() {
      this.$modalRouter.push({
        name: "ViewOptionsModal",
        props: {
          show: true,
          modalTitle: "View options",
        },
        modal: "show",
        on: {
          saveViewClicked: this.saveViewClicked,
          loadViewClicked: this.loadViewClicked,
          resetViewSettings: this.resetViewSettings,
        },
      });
    },
    restart() {
      EventBus.$emit("restart");
    },
  },
};
</script>

<style scoped lang="scss">
.base-layout {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.link {
  margin-right: 24px;
}
.stop-impersonation {
  display: inline;
  text-decoration: underline;
  color: white;
  margin-left: 10px;
  cursor: pointer;
}

.restart-container {
  display: flex;

  .restart {
    color: $blue-200;
    cursor: pointer;
    text-decoration: underline;
    align-self: center;

    &:hover {
      color: $blue-100;
    }

    &:active {
      color: $blue-300;
    }
  }
}

.action-bar .action-items .action-item {
  &.guide-me {
    background-color: $green-500;
    color: $dark-gray-900;
    cursor: pointer;
    text-decoration: none;
    padding-left: 1rem;
    padding-right: 1rem;
    border-color: $green-500;
    border-radius: 2px;

    &:hover {
      background-color: $green-300;
      border-color: $green-300;
    }
  }
}
</style>

<style lang="scss">
.dropdown-item-icon {
  width: 16px;
  margin-right: 15px;
}

.snotify-success {
  border-left: none;
  .snotify-icon {
    left: 32px;
    background-color: #00c776;
    border-radius: 40px;
    height: 42px;
    width: 42px;
    &::before {
      display: block;
      content: "";
      width: 16px;
      height: 16px;
      margin: 13px;
      background-color: #fff;
      fill: #ffffff;
      mask-image: url("../../../src/assets/img/signify-icons_02-check-12px.svg");
      -webkit-mask-image: url("../../../src/assets/img/signify-icons_02-check-12px.svg");
    }
  }
  .snotify-icon--success {
    background-image: none !important;
  }
}

.snotify-error {
  border-left: none;
  .snotify-icon {
    left: 32px;
    background-color: #ff0019;
    border-radius: 40px;
    height: 42px;
    width: 42px;
    &::before {
      display: block;
      content: "";
      width: 16px;
      height: 16px;
      margin: 13px;
      background-color: #fff;
      fill: #ffffff;
      mask-image: url("../../../src/assets/img/Signify-icons_40-error-16px.svg");
      -webkit-mask-image: url("../../../src/assets/img/Signify-icons_40-error-16px.svg");
    }
  }
  .snotify-icon--error {
    background-image: none !important;
  }

  .search-container {
    padding: 0 1.5rem;
  }
}
.snotify-warning {
  border-left: none;
  .snotify-icon {
    left: 32px;
    background-color: #ffc107;
    border-radius: 40px;
    height: 42px;
    width: 42px;
    &::before {
      display: block;
      content: "";
      width: 16px;
      height: 16px;
      margin: 13px;
      background-color: #fff;
      fill: #ffffff;
      mask-image: url("../../../src/assets/img/Signify-icons_44-notifications.svg");
      -webkit-mask-image: url("../../../src/assets/img/Signify-icons_44-notifications.svg");
    }
  }
  .snotify-icon--warning {
    background-image: none !important;
  }

  .search-container {
    padding: 0 1.5rem;
  }
}
</style>
